<template>
  <menu-content-container
    v-if="mode.model === 'text'"
    :header="$t('forms.rating_segment_appearance')"
    :border-bottom="false"
  >
    <content-row class="!block">
      <FontSizeColor
        :data-testid="`segment-text-settings`"
        block-name="segmentsAttributes"
        no-border
      />
    </content-row>
    <content-row>
      <color-picker
        :label="$t('forms.button_color')"
        data-testid="rating-segment-bg-color"
        position="left"
        :default-color="bgColor.model"
        :changed="bgColor.changed"
        :reset-tooltip="$t('forms.reset_value')"
        @color-change="bgColor.model = $event"
        @reset="bgColor.reset"
      ></color-picker>
    </content-row>
  </menu-content-container>
  <menu-content-container
    v-else
    :header="$t('forms.rating_segment_appearance')"
    :border-bottom="false"
  ></menu-content-container>

  <border-form
    :border="border.model"
    :default-border="border.defaultValue"
    :allow-directions="false"
    data-testid="rating-segment-border"
    :container-border="false"
    :changed="border.changed"
    @borderChanged="onBorderChanged"
    @reset="border.reset"
  />

  <menu-content-container>
    <content-row>
      <t-input-number
        v-model="borderRadius.model"
        data-testid="rating-segment-border-radius"
        unit=""
        :label="$t('forms.rating_border_radius')"
        :min="0"
        :max="40"
        :changed="borderRadius.changed"
        :reset-tooltip="$t('forms.reset_value')"
        @reset="borderRadius.reset"
      />
    </content-row>
  </menu-content-container>

  <menu-content-container
    v-if="mode.model === 'text'"
    :header="$t('forms.rating_segment_hover')"
    :border-full="!hover"
  >
    <template #header-action>
      <div
        class="flex cursor-pointer items-center font-inter text-xs font-medium leading-6 text-accent-1 transition duration-300 hover:opacity-80"
        data-testid="toggle-rating-hover"
        @click="toggleHover"
      >
        <div>
          {{ !hover ? $t("forms.add_hover") : $t("forms.remove_hover") }}
        </div>
        <PlusIcon v-if="!hover" />
        <Minus v-else />
      </div>
    </template>
    <transition name="slide-fade-borders" mode="out-in">
      <div v-if="hover">
        <content-row>
          <color-picker
            :label="$t('forms.button_color_hover')"
            data-testid="rating-hover-bg-color"
            position="left"
            :default-color="hoverBgColor"
            @color-change="hoverBgColor = $event"
          ></color-picker>
          <color-picker
            :label="$t('forms.button_text_color_hover')"
            data-testid="rating-hover-text-color"
            position="right"
            :default-color="hoverColor"
            @color-change="hoverColor = $event"
          ></color-picker>
        </content-row>
      </div>
    </transition>
    <content-row v-if="!hover">
      <t-note>{{ $t("layout.hover_text") }}</t-note>
    </content-row>
  </menu-content-container>
  <transition name="slide-fade-borders" mode="out-in">
    <border-form
      v-if="mode.model === 'text' && hover"
      :label="$t('forms.button_border_hover')"
      :border="hoverBorder"
      :allow-directions="false"
      data-testid="rating-hover-border"
      @borderChanged="onHoverBorderChanged"
    >
      <content-row :margin-top="12">
        <t-note>{{ $t("layout.hover_text") }}</t-note>
      </content-row>
    </border-form>
  </transition>

  <menu-content-container v-if="mode.model === 'image'" :padding-left-right="8">
    <TTabsToggle
      :header="$t('forms.rating_image_label')"
      toggle-show
      :default-displayed="showImageLabel"
      no-move
      data-testid="toggle-segment-image-label"
      @showBlock="showImageLabel = true"
      @hideBlock="showImageLabel = false"
    >
      <menu-content-container border-full :padding-left-right="8">
        <content-row class="!block">
          <FontSizeColor
            :data-testid="`segment-image-label-settings`"
            block-name="imageLabelAttributes"
            no-border
          />
        </content-row>
      </menu-content-container>
    </TTabsToggle>
  </menu-content-container>
</template>

<script setup lang="ts">
  import borderForm from "../borderForm.vue";
  import colorPicker from "../elements/colorPicker.vue";
  import FontSizeColor from "./fontSizeColorRating.vue";

  import { useMjmlStore } from "../../stores/mjmlStore";
  import { useBlockAttr } from "@/composables/useBlockAttr";
  import { TopolRatingBlock } from "@/schemas/template/blocks/ratingBlockSchema";

  import { TInputNumber, TNote, TTabsToggle } from "@topol/twig-ui";
  import { PlusIcon, Minus } from "@topol/icons";

  import { computed } from "vue";

  const mjmlStore = useMjmlStore();

  const segments = useBlockAttr({
    mode: "active-group",
    group: "segmentsAttributes",
  });

  const bgColor = segments.str("background-color", "#ffffff");
  const border = segments.nullable("border");
  const borderRadius = segments.px("border-radius", 8);
  const mode = segments.str("mode", "text");

  const showImageLabel = computed({
    get() {
      const block = mjmlStore.getActiveBlock as any;
      return block?.imageLabelAttributes?.show ?? false;
    },
    set(val: boolean) {
      const block = mjmlStore.getActiveBlock as any;
      const current = (block?.imageLabelAttributes || {}) as Record<
        string,
        any
      >;
      mjmlStore.updateBlockProp({
        prop: "imageLabelAttributes",
        value: { ...current, show: val },
      });
    },
  });

  function onBorderChanged({ border: newBorder }: { border: string }) {
    border.model = newBorder;
  }

  // --- Hover (custom — separate top-level prop on the block) ---
  const activeBlock = computed(
    () => mjmlStore.getActiveBlock as TopolRatingBlock | undefined
  );

  const hover = computed({
    get() {
      const block = activeBlock.value as any;
      return block?.segmentsHover;
    },
    set(val?: Record<string, string>) {
      mjmlStore.updateBlockProp({ prop: "segmentsHover", value: val });
    },
  });
  function toggleHover() {
    hover.value = hover.value ? undefined : {};
  }
  const hoverBgColor = computed({
    get() {
      return hover.value?.["background-color"] || bgColor.model || "#ffffff";
    },
    set(val: string) {
      hover.value = { ...(hover.value || {}), "background-color": val };
    },
  });
  const hoverColor = computed({
    get() {
      const block = activeBlock.value as any;
      return (
        hover.value?.color || block?.segmentsAttributes?.color || "#1a1a1a"
      );
    },
    set(val: string) {
      hover.value = { ...(hover.value || {}), color: val };
    },
  });
  const hoverBorder = computed(
    () => hover.value?.border || border.model || "1px solid #1a1a1a"
  );
  function onHoverBorderChanged({ border: newBorder }: { border: string }) {
    hover.value = { ...(hover.value || {}), border: newBorder };
  }
</script>
